// Iter 129 — Notify roster modal.
// PM clicks "📧 Notify staff" → choose New / Updated + optional note → send.
// Triggers POST /api/roster/notify-week. One consolidated email per staff
// member who has published shifts in the visible week.
import { useState } from 'react';
import axios from 'axios';
import { Send, X, Loader2 } from 'lucide-react';
import { API } from './_helpers';

export default function NotifyRosterModal({ rangeFrom, rangeTo, rangeLabel, onClose }) {
  const [kind, setKind] = useState('new');     // 'new' | 'updated'
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/roster/notify-week`,
        { from: rangeFrom, to: rangeTo, kind, message: message.trim() || null },
        { withCredentials: true });
      setResult(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not send notifications');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="notify-roster-modal"
           className="bg-card rounded-xl border max-w-md w-full shadow-2xl">
        <div className="px-5 py-3.5 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold" style={{ color: '#0d2644' }}>Notify staff</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" data-testid="notify-roster-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {result ? (
          <div className="p-5 space-y-3" data-testid="notify-roster-result">
            <div className="rounded-lg p-3 bg-green-50 border border-green-200 text-sm">
              <div className="font-bold text-green-800 mb-1">
                Notified {result.notified} staff member{result.notified === 1 ? '' : 's'}.
              </div>
              <div className="text-green-700/80 text-xs">
                {result.range} · {result.kind === 'new' ? 'New roster' : 'Roster updated'}
                {result.failures > 0 && ` · ${result.failures} failure(s)`}
              </div>
            </div>
            {result.details && result.details.length > 0 && (
              <ul className="text-xs text-muted-foreground space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                {result.details.map(d => (
                  <li key={d.email} className="flex justify-between">
                    <span className="truncate">{d.email}</span>
                    <span className="text-foreground font-semibold ml-2">{d.shifts} shift{d.shifts === 1 ? '' : 's'}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={onClose} data-testid="notify-roster-done"
                      className="px-4 py-1.5 rounded-lg bg-[#0d2644] text-white text-sm font-semibold">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-sm">
              <div className="font-semibold mb-1" style={{ color: '#0d2644' }}>Week</div>
              <div className="text-foreground">{rangeLabel}</div>
              <div className="text-xs text-muted-foreground mt-1">
                One email per staff member who has at least one published shift this week. Drafts are skipped.
              </div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: '#0d2644' }}>
                Notification type
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setKind('new')} data-testid="notify-roster-kind-new"
                        className={`text-sm font-semibold px-3 py-2 rounded-lg border transition-colors
                                    ${kind === 'new' ? 'bg-[#0d2644] text-white border-[#0d2644]' : 'bg-card hover:bg-muted/40'}`}>
                  New roster
                </button>
                <button onClick={() => setKind('updated')} data-testid="notify-roster-kind-updated"
                        className={`text-sm font-semibold px-3 py-2 rounded-lg border transition-colors
                                    ${kind === 'updated' ? 'bg-[#0d2644] text-white border-[#0d2644]' : 'bg-card hover:bg-muted/40'}`}>
                  Roster updated
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: '#0d2644' }}>
                Optional note from PM
              </label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={400}
                        rows={2} placeholder="e.g. Please accept by Friday. Public holiday on Wed."
                        data-testid="notify-roster-message"
                        className="w-full text-sm rounded-lg border bg-background px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#0d2644]/30" />
              <div className="text-[10px] text-muted-foreground mt-0.5 text-right">{message.length} / 400</div>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2.5 leading-relaxed">
              The email contains a friendly summary + a single <strong>&ldquo;Open SP staff portal&rdquo;</strong> button.
              Existing per-shift accept/decline emails are <strong>not</strong> sent again — this is a separate reminder.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} disabled={busy} data-testid="notify-roster-cancel"
                      className="text-sm font-semibold px-4 py-1.5 rounded-lg border hover:bg-muted/40">
                Cancel
              </button>
              <button onClick={send} disabled={busy} data-testid="notify-roster-send"
                      className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-[#0d2644] text-white inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send notifications
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
